import {
  type InfiniteData,
  type Query,
  type UseInfiniteQueryOptions,
  type UseQueryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';

import * as Sentry from '@sentry/react';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import {
  type ApiRoutersPortfoliosAssetsImagesCollectionsCollectionsImageViewFields,
  type SearchCollectionImagesV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesGetData,
  type SearchCollectionImagesV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesGetError,
  type SearchCollectionImagesV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesGetResponse,
  searchCollectionImagesV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesGet,
} from '../../generated';
import { PENDING_STALE_TIMEOUT_MS, getPendingImageTimestamps, pollWhilePending } from '../../utils/poll-while-pending';

export type SearchCollectionImagesQueryVariables =
  SearchCollectionImagesV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesGetData['path'] &
    NonNullable<SearchCollectionImagesV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesGetData['query']>;

export type SearchCollectionImagesQuery = {
  data: SearchCollectionImagesV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesGetResponse;
  total_count: number;
};

/**
 * Extracts the total_count from the Vizit-Total-Count response header
 */
function extractTotalCount(response: Response): number {
  const totalCountHeader = response.headers.get('vizit-total-count');
  return totalCountHeader ? Number.parseInt(totalCountHeader, 10) : 0;
}

export const useSearchCollectionImagesQuery = <
  TData = SearchCollectionImagesQuery,
  TError = SearchCollectionImagesV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesGetError,
>(
  {
    workspace_id,
    collection_id,
    sort_by,
    parent_id,
    source,
    exclude_source,
    image_ids,
    classification,
    limit,
    offset,
    sort_direction,
  }: SearchCollectionImagesQueryVariables,
  options?: Omit<UseQueryOptions<SearchCollectionImagesQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<SearchCollectionImagesQuery, TError, TData>({
    queryKey: [
      'SearchCollectionImages',
      {
        workspace_id,
        collection_id,
        sort_by,
        parent_id,
        source,
        exclude_source,
        image_ids,
        classification,
        limit,
        offset,
        sort_direction,
      },
    ],
    queryFn: async () => {
      const { data, response } =
        await searchCollectionImagesV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesGet({
          path: { workspace_id, collection_id },
          query: {
            sort_by: sort_by as ApiRoutersPortfoliosAssetsImagesCollectionsCollectionsImageViewFields | undefined,
            parent_id,
            source: source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
            exclude_source: exclude_source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
            image_ids: image_ids as string[] | undefined,
            classification,
            limit,
            offset,
            sort_direction,
          },
          throwOnError: true,
        });
      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
      };
    },
    ...options,
  });
};

useSearchCollectionImagesQuery.getKey = (variables: SearchCollectionImagesQueryVariables) => [
  'SearchCollectionImages',
  variables,
];

useSearchCollectionImagesQuery.fetcher = (
  {
    workspace_id,
    collection_id,
    sort_by,
    parent_id,
    source,
    exclude_source,
    image_ids,
    classification,
    limit,
    offset,
    sort_direction,
  }: SearchCollectionImagesQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data, response } =
      await searchCollectionImagesV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesGet({
        path: { workspace_id, collection_id },
        query: {
          sort_by: sort_by as ApiRoutersPortfoliosAssetsImagesCollectionsCollectionsImageViewFields | undefined,
          parent_id,
          source: source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
          exclude_source: exclude_source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
          image_ids: image_ids as string[] | undefined,
          classification,
          limit,
          offset,
          sort_direction,
        },
        throwOnError: true,
      });
    return {
      data: data ?? [],
      total_count: extractTotalCount(response),
    };
  };
};

export type SearchCollectionImagesInfiniteQueryVariables = Omit<SearchCollectionImagesQueryVariables, 'offset'>;

export const useSearchCollectionImagesInfiniteQuery = <
  TData = SearchCollectionImagesQuery,
  TError = SearchCollectionImagesV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesGetError,
>(
  {
    workspace_id,
    collection_id,
    sort_by,
    parent_id,
    source,
    exclude_source,
    image_ids,
    classification,
    limit,
    sort_direction,
  }: SearchCollectionImagesInfiniteQueryVariables,
  options?: Omit<
    UseInfiniteQueryOptions<SearchCollectionImagesQuery, TError, TData>,
    'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
  >,
) => {
  return useInfiniteQuery<SearchCollectionImagesQuery, TError, TData>({
    queryKey: [
      'SearchCollectionImagesInfinite',
      {
        workspace_id,
        collection_id,
        sort_by,
        parent_id,
        source,
        exclude_source,
        image_ids,
        classification,
        limit,
        sort_direction,
      },
    ],
    queryFn: async ({ pageParam = 0 }) => {
      const { data, response } =
        await searchCollectionImagesV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesGet({
          path: { workspace_id, collection_id },
          query: {
            sort_by: sort_by as ApiRoutersPortfoliosAssetsImagesCollectionsCollectionsImageViewFields | undefined,
            parent_id,
            source: source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
            exclude_source: exclude_source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
            image_ids: image_ids as string[] | undefined,
            classification,
            limit,
            offset: pageParam as number,
            sort_direction,
          },
          throwOnError: true,
        });

      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
      };
    },
    getNextPageParam: (lastPage, allPages) => {
      const pageSize = limit || 12; // Default to 12 if no limit specified
      const currentOffset = (allPages.length - 1) * pageSize;
      const nextOffset = currentOffset + pageSize;

      // Check if we have more data to fetch
      if (lastPage.data.length < pageSize || nextOffset >= lastPage.total_count) {
        return undefined; // No more pages
      }

      return nextOffset;
    },
    getPreviousPageParam: (_, allPages) => {
      // Return undefined when we're on the first/only page (initial load)
      // Return a value when we have multiple pages (subsequent loads)
      return allPages.length <= 1 ? undefined : 0;
    },
    initialPageParam: 0,
    refetchInterval: pollWhilePending(
      (query: Query<SearchCollectionImagesQuery, TError, InfiniteData<SearchCollectionImagesQuery, unknown>>) =>
        getPendingImageTimestamps((query.state.data?.pages ?? []).flatMap((page) => page.data)),
      {
        onTimeout: (pendingCount) => {
          Sentry.withScope((scope) => {
            scope.setFingerprint([
              'GET',
              '/v1/workspaces/{workspace_id}/assets/images/collections/{collection_id}/images',
              'polling_timeout',
            ]);

            Sentry.captureException(
              new Error('Images still pending after stale window for search collection images query.'),
              {
                level: 'warning',
                tags: {
                  feature: SENTRY_FEATURE_TAGS.Collection,
                  stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Collection].collectionAssetPolling,
                },
                extra: {
                  workspace_id,
                  collection_id,
                  pending_count: pendingCount,
                  stale_window_ms: PENDING_STALE_TIMEOUT_MS,
                  query_variables: {
                    workspace_id,
                    collection_id,
                    sort_by,
                    parent_id,
                    source,
                    exclude_source,
                    image_ids,
                    classification,
                    limit,
                    sort_direction,
                  },
                },
              },
            );
          });
        },
      },
    ),
    refetchIntervalInBackground: true,
    ...options,
  });
};
